import ENV_VARIABLES from "@/env";
import { ClusterType } from "@/types";

/**
 * Hours of continuous use shown as one "month" in PAYG cost estimates.
 * 730 (365 * 24 / 12) matches the API's monthly-to-hourly conversion.
 */
export const PAYG_HOURS_PER_MONTH = 730;

/** Matches the API's $10 minimum on credits top-ups. */
export const PAYG_MIN_TOPUP_USD = 10;

/**
 * PAYG replaces fixed-duration rental on the V2 container/VM deploy flows.
 * "caas"/"container" both appear as cluster type identifiers depending on the caller.
 */
const PAYG_CLUSTER_TYPES: string[] = [ClusterType.CONTAINER, "caas", ClusterType.VMAAS];

export const isPaygCluster = (clusterType?: string) =>
  Boolean(ENV_VARIABLES.FEATUREFLAG_PAYG_ENABLED) &&
  Boolean(clusterType && PAYG_CLUSTER_TYPES.includes(clusterType));

/**
 * Credits purchase to suggest before a PAYG deploy: enough to cover the next
 * hour of usage, never below the $10 payment minimum.
 */
export const getPaygSuggestedTopUp = (hourlyCost: number, creditBalance: number) => {
  const shortfall = Math.max(0, hourlyCost - creditBalance);
  return Math.max(PAYG_MIN_TOPUP_USD, Math.ceil(shortfall * 100) / 100);
};

export const getPaygMonthlyEstimate = (hourlyCost: number) => hourlyCost * PAYG_HOURS_PER_MONTH;

/** Below this, credits are close enough to running out that the UI warns. */
export const PAYG_LOW_RUNWAY_HOURS = 24;

/** Below this, clusters are hours from stopping and the warning escalates to red. */
export const PAYG_CRITICAL_RUNWAY_HOURS = 2;

export type PaygRunwayLevel = "ok" | "low" | "critical";

/** How urgently the UI should colour the runway: red under 2h, yellow under a day. */
export const getPaygRunwayLevel = (hours: number | null): PaygRunwayLevel => {
  if (hours === null) return "ok";
  if (hours < PAYG_CRITICAL_RUNWAY_HOURS) return "critical";
  if (hours < PAYG_LOW_RUNWAY_HOURS) return "low";
  return "ok";
};

/** How long the credits balance covers the current burn; null when nothing is burning. */
export const getPaygRunwayHours = (creditBalance: number, hourlyRate: number | null) => {
  if (!hourlyRate || hourlyRate <= 0) return null;
  return Math.max(0, creditBalance) / hourlyRate;
};

/**
 * Runway as a rough human span ("~3 days"). Deliberately not date.ts's formatSeconds:
 * its weeks bucket divides by 648000 instead of 604800, and runways often exceed a week.
 */
export const formatPaygRunway = (hours: number | null) => {
  if (hours === null) return "--";
  if (hours <= 0) return "none left";
  if (hours >= 30 * 24) return "30+ days";
  if (hours >= 48) return `~${Math.floor(hours / 24)} days`;
  if (hours >= 1) return `~${Math.floor(hours)} hours`;
  return `~${Math.max(1, Math.floor(hours * 60))} minutes`;
};
